import { motion } from 'framer-motion'
import type { ReactNode } from 'react'
import { useId } from 'react'
import type { IconType } from 'react-icons'
import { FaCamera } from 'react-icons/fa'
import {
  GiCheckMark,
  GiCycle,
  GiFactory,
  GiGasPump,
  GiMagnifyingGlass,
  GiNetworkBars,
  GiRobotGrab,
  GiRocket,
} from 'react-icons/gi'

import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { cn } from '@/lib/utils'
import IsoLogo from '../../../assets/images/iso-dark-mode.png'

const BentoCardSkeletonBody = ({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) => {
  return <div className={cn('', className)}>{children}</div>
}

const BentoCardContent = ({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) => {
  return <div className={cn('p-6', className)}>{children}</div>
}

const BentoCardTitle = ({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) => {
  return (
    <h3
      className={cn(
        'font-sans text-sm font-medium tracking-tight text-white',
        className,
      )}
    >
      {children}
    </h3>
  )
}

const BentoCardDescription = ({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) => {
  return (
    <h3
      className={cn(
        'mt-2 max-w-xs font-sans text-sm font-normal tracking-tight text-white/70',
        className,
      )}
    >
      {children}
    </h3>
  )
}

const BentoCard = ({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) => {
  return (
    <motion.div
      whileHover='animate'
      className={cn(
        'group isolate flex flex-col overflow-hidden rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 shadow-[0_8px_32px_0_rgba(31,38,135,0.37)]',
        className,
      )}
    >
      {children}
    </motion.div>
  )
}

const AgentFeatureVisual = ({
  icon: Icon,
  accent,
  label,
}: {
  icon: IconType
  accent?: string
  label?: string
}) => {
  return (
    <div className='relative h-full overflow-hidden rounded-t-[32px]'>
      {accent && (
        <div
          className={cn(
            'absolute inset-0 bg-gradient-to-br opacity-60',
            accent,
          )}
        />
      )}
      <div className='absolute inset-0 bg-white/5' />
      <div className='relative flex h-full flex-col items-center justify-center gap-4 px-8 py-10 text-white'>
        <div className='flex h-16 w-16 items-center justify-center rounded-full border border-white/30 bg-white/10 backdrop-blur-sm'>
          <Icon className='text-3xl' />
        </div>
        {label && (
          <span className='rounded-full border border-white/20 bg-white/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-white/70'>
            {label}
          </span>
        )}
      </div>
    </div>
  )
}

const DataCleaningAnimation = () => {
  const stages = [
    {
      label: 'Raw Feed',
      description:
        'Import documents, PDFs, and flat files from any partner system.',
      icon: GiFactory,
    },
    {
      label: 'Normalize',
      description:
        'Structure data with Crossroads templates and universal mappings.',
      icon: GiCheckMark,
    },
    {
      label: 'Validate',
      description:
        'Run quality checks and auto-resolve duplicates before data moves on.',
      icon: GiRobotGrab,
    },
    {
      label: 'Publish',
      description:
        'Distribute clean payloads to Crossroads and downstream systems.',
      icon: GiGasPump,
    },
  ]

  return (
    <div className='relative flex h-full flex-col overflow-hidden rounded-[28px] border border-white/15 bg-white/8 p-6 text-white shadow-[0_30px_120px_-60px_rgba(0,0,0,0.6)] md:p-8'>
      <div
        className='absolute inset-0'
        style={{
          background:
            'linear-gradient(145deg, rgba(147,137,210,0.45) 0%, rgba(45,40,75,0.2) 100%)',
        }}
      />
      <div className='relative flex items-center justify-between text-xs font-semibold uppercase tracking-[0.4em] text-white/70'>
        <span>Data Pipeline</span>
        <span>Automation</span>
      </div>
      <div className='relative mt-8 flex-1'>
        <div className='absolute left-6 top-4 bottom-4 w-px bg-white/25' />
        <div className='relative ml-0 flex h-full flex-col gap-6'>
          {stages.map((stage, index) => (
            <motion.div
              key={stage.label}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 + index * 0.15 }}
              className='relative flex gap-4 rounded-2xl border border-white/15 bg-white/10 px-5 py-4 backdrop-blur-sm'
            >
              <div className='relative flex h-10 w-10 items-center justify-center rounded-full border border-white/30 bg-white/10'>
                <stage.icon className='h-5 w-5' />
              </div>
              <div className='flex flex-1 flex-col gap-1'>
                <div className='text-sm font-semibold tracking-wide text-white'>
                  {stage.label}
                </div>
                <p className='text-xs text-white/70'>{stage.description}</p>
              </div>
              <div className='absolute -left-6 top-1/2 -translate-y-1/2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-white/80'>
                {index + 1}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
      <div className='relative mt-6 grid gap-3 md:grid-cols-2'>
        <div className='flex items-center gap-3 rounded-xl border border-white/10 bg-white/15 px-4 py-3 text-xs text-white/85 backdrop-blur'>
          <GiRobotGrab className='h-4 w-4 text-white' />
          Noise removed · Duplicates resolved
        </div>
        <div className='flex items-center gap-3 rounded-xl border border-white/10 bg-white/15 px-4 py-3 text-xs text-white/85 backdrop-blur'>
          <GiNetworkBars className='h-4 w-4 text-white' />
          Clean dataset delivered to Crossroads
        </div>
      </div>
    </div>
  )
}

const IntegrationReportingAnimation = () => {
  const Beam = ({
    delay,
    duration,
    className,
  }: {
    delay?: number
    duration?: number
    className?: string
  }) => {
    const id = useId()

    return (
      <motion.svg
        viewBox='0 0 600 1'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
        className={cn('absolute inset-x-0 w-full', className)}
      >
        <motion.path d='M0 0.5H600' stroke={`url(#beam-${id})`} />
        <defs>
          <motion.linearGradient
            id={`beam-${id}`}
            gradientUnits='userSpaceOnUse'
            initial={{ x1: '0%', x2: '-5%' }}
            animate={{ x1: '110%', x2: '105%' }}
            transition={{
              duration: duration ?? 2.2,
              ease: 'linear',
              repeat: Infinity,
              delay,
            }}
          >
            <stop stopColor='rgba(147,137,210,0)' />
            <stop stopColor='rgba(76,154,214,0.9)' />
            <stop offset='1' stopColor='rgba(147,137,210,0)' />
          </motion.linearGradient>
        </defs>
      </motion.svg>
    )
  }

  const metricCards = [
    {
      title: '99.7% uptime',
      description: '14 active alerts resolved in the last 7 days.',
    },
    {
      title: 'Forecasted load variance ↓ 18%',
      description:
        'Predictive dashboards flag exceptions before they impact delivery windows.',
    },
  ]

  return (
    <div className='relative flex h-full flex-col overflow-hidden rounded-[28px] border border-white/15 bg-white/10 p-6 text-white shadow-[0_30px_120px_-60px_rgba(0,0,0,0.6)] md:p-8'>
      <div
        className='absolute inset-0'
        style={{
          background:
            'linear-gradient(150deg, rgba(76,154,214,0.45) 0%, rgba(45,40,75,0.2) 100%)',
        }}
      />
      <div className='relative flex items-start justify-between gap-3'>
        <div>
          <p className='text-xs font-semibold uppercase tracking-[0.35em] text-white/70'>
            Integration Signal
          </p>
          <h4 className='mt-3 text-lg font-semibold'>
            Crossroads Network Monitor
          </h4>
        </div>
        <span className='rounded-full border border-white/25 bg-white/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-white/70'>
          Live
        </span>
      </div>
      <div className='relative mt-6 flex-1 overflow-hidden rounded-2xl border border-white/10 bg-white/10 backdrop-blur'>
        <Beam className='top-6' delay={0.2} />
        <Beam className='top-12' delay={0.6} duration={2.6} />
        <Beam className='top-18' delay={1.1} duration={1.9} />
        <Beam className='top-24' delay={1.6} duration={2.8} />
        <div className='absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(76,154,214,0.25),transparent_70%)]' />
        <div className='relative flex h-full flex-col justify-center gap-4 px-6'>
          {metricCards.map((metric, index) => (
            <motion.div
              key={metric.title}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 + index * 0.25 }}
            >
              <Card className='border-white/20 bg-white/15 text-left text-white backdrop-blur-xl'>
                <CardHeader className='space-y-2 p-5'>
                  <CardTitle className='text-lg font-semibold text-white md:text-xl'>
                    {metric.title}
                  </CardTitle>
                  <CardDescription className='text-xs text-white/70 md:text-sm'>
                    {metric.description}
                  </CardDescription>
                </CardHeader>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
      <div className='relative mt-6 flex flex-col gap-3'>
        {[
          {
            icon: GiRocket,
            title: 'Integration Health',
            stat: '99.7%',
            description: 'Automated routing and resolution keep uptime high.',
          },
          {
            icon: GiCycle,
            title: 'Data Mappings & Translations',
            stat: '↓18%',
            description:
              'Forecast variance trending down with proactive interventions.',
          },
          {
            icon: GiNetworkBars,
            title: 'Alerts Resolved',
            stat: '14',
            description:
              'Incidents closed over the last seven days across the network.',
          },
        ].map((card) => (
          <div
            key={card.title}
            className='relative overflow-hidden rounded-2xl border border-white/15 bg-white/15 px-5 py-4 backdrop-blur-lg'
          >
            <div className='absolute inset-0 bg-gradient-to-br from-white/25 via-white/10 to-transparent' />
            <div className='relative flex items-center justify-between'>
              <div className='flex items-center gap-3'>
                <card.icon className='h-5 w-5 text-white' />
                <span className='text-sm font-semibold text-white'>
                  {card.title}
                </span>
              </div>
              <span className='rounded-full border border-white/10 bg-white/20 px-3 py-1 text-xs font-semibold text-white/85'>
                {card.stat}
              </span>
            </div>
            <p className='relative mt-3 text-xs text-white/70'>
              {card.description}
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}

type AgentFeature = {
  title: string
  description: string
  icon: IconType
  accent?: string
  label?: string
  visual?: ReactNode
}

const agentFeatures: AgentFeature[] = [
  {
    title: 'AI-powered BOL Capture',
    description:
      'Turn any paper BOL into a perfect eBOL with any phone or tablet camera.',
    icon: FaCamera,
    accent: 'from-[#4C9AD6]/75 to-transparent',
    label: 'Vision Agent',
  },
  {
    title: 'ATG Polling Agent',
    description:
      'Poll any automatic tank gauge in real time - any terminal, any time, anywhere.',
    icon: GiGasPump,
    accent: 'from-[#5E6CE3]/70 to-transparent',
    label: 'Tank Agent',
  },
  {
    title: 'Modern Web and Email Scraping',
    description:
      'Digitize and integrate pricing, inventory, and order data from any web or email source automatically.',
    icon: GiMagnifyingGlass,
    accent: 'from-[#3D3861]/75 via-[#3D3861]/25 to-transparent',
    label: 'Capture Agent',
  },
  {
    title: 'Intelligent Data Cleaning',
    description:
      'Clean up and organize mountains of legacy data so it connects seamlessly into Crossroads.',
    icon: GiRobotGrab,
    visual: <DataCleaningAnimation />,
  },
  {
    title: 'Dynamic Integration Reporting',
    description:
      'Dashboards and proactive alerting that surface issues before they impact operations.',
    icon: GiNetworkBars,
    visual: <IntegrationReportingAnimation />,
  },
]

export const BentoGridSection = () => {
  const renderFeatureCard = (
    feature: AgentFeature,
    cardClassName?: string,
    skeletonClassName?: string,
  ) => (
    <BentoCard
      key={feature.title}
      className={cn('flex flex-col', cardClassName)}
    >
      <BentoCardContent className='p-8'>
        <BentoCardTitle className='text-base md:text-lg'>
          {feature.title}
        </BentoCardTitle>
        <BentoCardDescription className='text-sm md:text-base max-w-sm'>
          {feature.description}
        </BentoCardDescription>
      </BentoCardContent>
      <BentoCardSkeletonBody
        className={cn(
          feature.visual ? 'mt-0 flex-1 h-full' : 'mt-auto h-48',
          skeletonClassName,
        )}
      >
        {feature.visual ?? (
          <AgentFeatureVisual
            icon={feature.icon}
            accent={feature.accent}
            label={feature.label}
          />
        )}
      </BentoCardSkeletonBody>
    </BentoCard>
  )

  const stackedFeatures = agentFeatures.slice(0, 3)
  const pillarFeatures = agentFeatures.slice(3)

  return (
    <section className='relative z-20 py-40 bg-gradient-to-br from-[#2D284B] via-[#3D3861] to-[#4D4875] overflow-hidden'>
      <div className='absolute -right-10 -bottom-10 opacity-5'>
        <img src={IsoLogo} alt='' className='w-96 h-96 object-contain' />
      </div>
      <div className='absolute -left-10 top-20 opacity-5'>
        <img
          src={IsoLogo}
          alt=''
          className='w-96 h-96 object-contain rotate-45'
        />
      </div>

      <div className='relative z-10 px-8 lg:px-12 xl:px-16'>
        <div className='mx-auto w-full'>
          <h2 className='font-sans text-3xl font-medium tracking-tight md:text-5xl lg:text-6xl text-white'>
            Crossroads Agents and Microservices
          </h2>
          <p className='mt-6 max-w-2xl text-base md:text-lg text-white/80'>
            Deploy purpose-built automation to connect Crossroads with every
            document, device, and data source in your operation.
          </p>
          <div className='mt-24 grid grid-cols-1 gap-4 md:gap-6 xl:grid-cols-[2fr_1fr_1fr]'>
            <div className='flex flex-col gap-4 md:gap-6'>
              {stackedFeatures.map((feature) => renderFeatureCard(feature))}
            </div>
            {pillarFeatures.map((feature) =>
              renderFeatureCard(feature, 'xl:h-full', 'xl:flex-1 xl:h-full'),
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
